import React, { useCallback, useMemo } from 'react';
import {
  ProjectContent,
  ProjectItem,
  ProjectList,
  Document,
  Icon,
  Title,
  Text,
  Point,
  ProgressDiv,
  AssignContent,
  AssignGroup,
  Plus,
  Box,
  Bottom,
  ProjectListTopSection,
  TitleSection,
  SmallDiv,
  ProgressStatus,
  TitleTopSection,
  Badge,
  BadgeTitle
} from './styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { useMobileDevice } from '../../global/useMobile';
import {
  AVATAR_BORDER,
  ICON_COLORS,
  ICON_SIZE,
  PROJECT_ICON_TYPE,
  PROJECT_STATUS,
  PROJECT_STATUS_COLOR
} from '../../global/constants';
import Avatar from '../avatar';
import ProjectIconAvatar from '../projectIconAvatar';
import { ProjectModel } from '../../mobx/models/project';
import { isEmpty } from '../../helpers/common';
import Tooltip from '../tooltip';
import ProgressBar from '../progressBar';

interface Props {
  onClickFavorite?: (e: React.SyntheticEvent) => void;
  onClickProjectList: () => void;
  showCompanyName?: boolean;
  projectName: string;
  projectId: string;
  projectCustomer?: string;
  projectIcon?: ProjectModel['icon'];
  progressData?: ProjectModel['progressData'];
  projectUsers: ProjectModel['users'];
  applyTopSpacing?: boolean;
  isArchived?: boolean;
}

const ProjectCard: React.FC<Props> = ({
  onClickFavorite,
  onClickProjectList,
  showCompanyName = true,
  projectName,
  projectId,
  projectCustomer,
  projectIcon,
  progressData,
  projectUsers,
  applyTopSpacing = false,
  isArchived = false
}) => {
  // other variables
  const isMobile = useMobileDevice();

  const renderProjectDetails = () => {
    return (
      <Document>
        <Title>{projectName}</Title>
        {showCompanyName && !isEmpty(projectCustomer) && <Text>{`for ${projectCustomer}`}</Text>}
      </Document>
    );
  };

  const renderArchiveBadge = useMemo(() => {
    return (
      <Badge>
        <BadgeTitle>Archived</BadgeTitle>
      </Badge>
    );
  }, []);

  const onClickProjectCard = useCallback(() => {
    if (isArchived) return;
    onClickProjectList();
  }, [isArchived, onClickProjectList]);

  return (
    <ProjectContent
      role='button'
      key={projectId}
      onClick={onClickProjectCard}
      isArchived={isArchived}
      applyTopSpacing={applyTopSpacing}>
      <Box>
        <ProjectItem>
          <ProjectList>
            <ProjectListTopSection>
              <TitleSection>
                <TitleTopSection>
                  <ProjectIconAvatar
                    size={isMobile ? 36 : 30}
                    type={projectIcon?.type ?? PROJECT_ICON_TYPE.DEFAULT}
                    reference={projectIcon?.reference || ''}
                    backgroundColor={projectIcon?.color ? ICON_COLORS[projectIcon?.color] : ''}
                    projectName={projectName || ''}
                    borderRadius={isMobile ? '8px' : '6px'}
                    iconSize={isMobile ? 20 : 16}
                  />
                  {!isMobile && isArchived && renderArchiveBadge}
                </TitleTopSection>
                {isMobile && renderProjectDetails()}
              </TitleSection>
              {onClickFavorite && (
                <Icon
                  role='button'
                  onClick={(e) => {
                    onClickFavorite(e);
                  }}>
                  <SVGIcon
                    name='fill-star-icon'
                    width={ICON_SIZE.MD}
                    height={ICON_SIZE.MD}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--accent-highlight)'
                  />
                </Icon>
              )}
            </ProjectListTopSection>
            {!isMobile && renderProjectDetails()}
          </ProjectList>
        </ProjectItem>
        <Bottom>
          {/* if archived project then hide progress bar in desktop screen   */}
          {!isMobile ? (
            !isArchived && (
              <Tooltip
                maxWidth={'max-content'}
                placement='top'
                title={
                  <Point color={PROJECT_STATUS_COLOR[progressData?.status || 1]}>
                    <ProgressStatus>
                      {`${progressData?.statusText || 'Not Started yet'}`}
                      {progressData?.status !== PROJECT_STATUS.NOT_STARTED && ` (${progressData?.progress || 0}%)`}
                    </ProgressStatus>
                  </Point>
                }>
                <ProgressDiv>
                  <ProgressBar
                    percent={progressData?.progress || 0}
                    progressColor={PROJECT_STATUS_COLOR[progressData?.status || 1]}
                    showInfo={false}
                    className='progressBar'
                  />
                </ProgressDiv>
              </Tooltip>
            )
          ) : // if archived project then show archive badge in responsive screen
          isArchived ? (
            renderArchiveBadge
          ) : (
            <Point>
              <ProgressStatus>
                <SmallDiv color={PROJECT_STATUS_COLOR[progressData?.status || PROJECT_STATUS.NOT_STARTED]} />
                {progressData?.statusText || 'Not Started yet'}{' '}
                {progressData?.status !== PROJECT_STATUS.NOT_STARTED && `(${progressData?.progress || 0}%)`}
              </ProgressStatus>
            </Point>
          )}
          <AssignContent>
            <AssignGroup>
              <ul>
                {projectUsers?.map((item, index) => {
                  if (index > 3) return null;
                  return (
                    <li key={item?.id}>
                      <Avatar
                        imgSrc={item?.profile_photo || ''}
                        name={item?.name ? item.name : 'U N'}
                        size={22}
                        borderVariant={AVATAR_BORDER.THIN}
                        className='avatar'
                      />
                    </li>
                  );
                })}
                {projectUsers?.length > 4 && (
                  <li className='plus-icon'>
                    <Plus>+{projectUsers?.length - 4}</Plus>
                  </li>
                )}
              </ul>
            </AssignGroup>
          </AssignContent>
        </Bottom>
      </Box>
    </ProjectContent>
  );
};

export default ProjectCard;
